import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { Navigation } from 'app/core/navigation/navigation.types';
import {
  Observable,
  ReplaySubject,
  tap,
  combineLatest,
  map,
  switchMap,
  of,
  startWith,
} from 'rxjs';
import { AppService } from 'app/core/app/app.service';
import { App } from 'app/core/app/app.model';

/**
 * IDs of navigation items that are scoped to a specific project.
 * These items will have their links prefixed with /projects/{projectId} when a project is selected,
 * and will be hidden when no project is selected.
 */
const PROJECT_SCOPED_NAV_IDS = [
  'divider-1',
  'project-settings',
  'voice',
  'fleet',
  'cortex',
  'rooms',
];

@Injectable({ providedIn: 'root' })
export class NavigationService {
  private _httpClient = inject(HttpClient);
  private _appService = inject(AppService);
  private _navigation: ReplaySubject<Navigation> =
    new ReplaySubject<Navigation>(1);
  private _baseNavigation: Navigation | null = null;

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for navigation.
   * Returns navigation with links dynamically updated based on the active app.
   */
  get navigation$(): Observable<Navigation> {
    return combineLatest([
      this._navigation.asObservable(),
      this._appService.activeApp$.pipe(startWith(null)),
    ]).pipe(
      map(([navigation, activeApp]) =>
        this.updateNavigationLinks(navigation, activeApp),
      ),
    );
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Get all navigation data
   */
  get(): Observable<Navigation> {
    return this._httpClient.get<Navigation>('api/common/navigation').pipe(
      tap((navigation) => {
        this._baseNavigation = navigation;
        this._navigation.next(navigation);
      }),
    );
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Private methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Update navigation links based on the active app.
   * - App-scoped items get their links prefixed with /projects/{projectId} when they are not collapsable
   * - When no app is selected, app-scoped items are hidden
   * - Recursively processes children for collapsable items
   */
  private updateNavigationLinks(
    navigation: Navigation,
    activeApp: App | null,
  ): Navigation {
    const updateItems = (items: FuseNavigationItem[]): FuseNavigationItem[] => {
      return items
        .filter((item) => {
          // Hide project-scoped items when no project is selected
          if (!activeApp && PROJECT_SCOPED_NAV_IDS.includes(item.id)) {
            return false;
          }
          return true;
        })
        .map((item) => {
          // Recursively update children if present (for collapsable items)
          if (item.children) {
            return {
              ...item,
              children: updateItems(item.children),
            };
          }
          // Update links for project-scoped items (non-collapsable)
          if (activeApp && PROJECT_SCOPED_NAV_IDS.includes(item.id)) {
            const basePath =
              item.id === 'project-settings' ? 'settings' : item.id;
            return {
              ...item,
              link: `/projects/${activeApp.id}/${basePath}`,
            };
          }
          return item;
        });
    };

    return {
      compact: updateItems([...navigation.compact]),
      default: updateItems([...navigation.default]),
      futuristic: updateItems([...navigation.futuristic]),
      horizontal: updateItems([...navigation.horizontal]),
    };
  }
}
